import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import {
  DynamicModuleService,
  PtsProjectService,
  SchemaService,
} from "@/services/APIs";
import { ProColumns } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Select as AntSelect } from "antd";
import { Edit2, List, Plus, Trash } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Label } from "./ui/label";

const SYSTEM_FIELDS = [
  "id",
  "is_active",
  "created_on",
  "updated_on",
  "created_by",
  "updated_by",
  "tenant",
  "created_by_id",
  "updated_by_id",
  "tenant_id",
];

function getRecordId(record: Record<string, unknown>): string {
  if (!record || typeof record !== "object") {
    return "";
  }

  const value =
    record.id ?? record.ID ?? record.Id ?? record._id ?? record.pk ?? "";

  return value !== null ? String(value).trim() : "";
}

const hasSchema = (
  json: Record<string, unknown> | string | null | undefined
): json is Record<string, unknown> => {
  return (
    typeof json !== "string" &&
    !!json &&
    typeof json === "object" &&
    "schema" in json
  );
};

const hasXComponent = (
  property: unknown
): property is { "x-component": string; title: string } => {
  return (
    !!property &&
    typeof property === "object" &&
    typeof (property as { "x-component"?: string })["x-component"] ===
      "string" &&
    typeof (property as { title?: string }).title === "string"
  );
};

function resolveProjectLabel(
  projectId: string | undefined,
  projectOptions: Array<{ value: string; label: string }>
): string {
  const trimmed = projectId?.trim();
  if (!trimmed) {
    return "—";
  }

  return (
    projectOptions.find(option => option.value === trimmed)?.label ?? trimmed
  );
}

export interface PtsSchemaCrudPageProps {
  schemaName: string;
  pageTitle: string;
  pageDescription: string;
  entityName: string;
  /** Field names (lowercase) that store project IDs and should display project names */
  projectLookupFields?: string[];
  /** Field names (lowercase) to place at the end of the table, before Actions */
  moveColumnsToEnd?: string[];
  /**
   * Ensure these columns appear in the list even if missing from column-grid.
   * Example: [{ field_name: "gst_no", field_title: "GST No" }]
   */
  ensureListColumns?: Array<{ field_name: string; field_title: string }>;
  /**
   * Place a column immediately after another column.
   * Keys/values are field names (case-insensitive).
   * Example: { gst_no: "gst" }
   */
  placeColumnsAfter?: Record<string, string>;
  /**
   * When true, project Select fields in the form use
   * /pts_app/project-summary/open-project-list/ options.
   */
  useOpenProjectListInForm?: boolean;
}

function isAddressLikeField(fieldName: string, fieldTitle: string): boolean {
  return (
    fieldName === "address" ||
    fieldName.includes("address") ||
    fieldTitle === "address" ||
    fieldTitle.includes("address")
  );
}

function isProjectLikeField(
  fieldName: string,
  fieldTitle: string,
  projectLookupFieldSet: Set<string>
): boolean {
  return (
    projectLookupFieldSet.has(fieldName) ||
    fieldName.includes("project") ||
    fieldTitle.includes("project") ||
    fieldTitle.includes("select project")
  );
}

type GridField = { field_name: string; field_title?: string };

function ensureAndOrderGridFields(
  rawFields: GridField[],
  ensureListColumns: Array<{ field_name: string; field_title: string }>,
  placeColumnsAfter: Record<string, string>,
  moveColumnsToEnd: string[]
): GridField[] {
  const fields = [...rawFields];
  const existingNames = new Set(
    fields.map(field => String(field.field_name).toLowerCase())
  );

  ensureListColumns.forEach(column => {
    const fieldName = column.field_name.toLowerCase();
    if (!existingNames.has(fieldName)) {
      fields.push({
        field_name: column.field_name,
        field_title: column.field_title,
      });
      existingNames.add(fieldName);
    }
  });

  const moveToEndSet = new Set(
    moveColumnsToEnd.map(field => field.toLowerCase())
  );

  const orderedFields =
    moveToEndSet.size === 0
      ? fields
      : [
          ...fields.filter(field => {
            const fieldName = String(field.field_name).toLowerCase();
            const fieldTitle = String(field.field_title ?? "").toLowerCase();
            const shouldMove =
              moveToEndSet.has(fieldName) ||
              (moveToEndSet.has("address") &&
                isAddressLikeField(fieldName, fieldTitle));
            return !shouldMove;
          }),
          ...fields.filter(field => {
            const fieldName = String(field.field_name).toLowerCase();
            const fieldTitle = String(field.field_title ?? "").toLowerCase();
            return (
              moveToEndSet.has(fieldName) ||
              (moveToEndSet.has("address") &&
                isAddressLikeField(fieldName, fieldTitle))
            );
          }),
        ];

  Object.entries(placeColumnsAfter).forEach(([targetField, afterField]) => {
    const targetName = targetField.toLowerCase();
    const afterName = afterField.toLowerCase();

    const matchesField = (field: GridField, name: string) => {
      const fieldName = String(field.field_name).toLowerCase();
      const fieldTitle = String(field.field_title ?? "")
        .toLowerCase()
        .replace(/\s+/g, "_");
      return fieldName === name || fieldTitle === name;
    };

    const targetIndex = orderedFields.findIndex(field =>
      matchesField(field, targetName)
    );
    const afterIndex = orderedFields.findIndex(field =>
      matchesField(field, afterName)
    );

    if (targetIndex < 0 || afterIndex < 0 || targetIndex === afterIndex + 1) {
      return;
    }

    const [target] = orderedFields.splice(targetIndex, 1);
    const updatedAfterIndex = orderedFields.findIndex(field =>
      matchesField(field, afterName)
    );
    orderedFields.splice(updatedAfterIndex + 1, 0, target);
  });

  return orderedFields;
}

function injectOpenProjectListIntoSchema(
  schemaInput: unknown,
  projectLookupFields: string[],
  projectOptions: Array<{ value: string; label: string }>
): unknown {
  if (!schemaInput || typeof schemaInput !== "object") {
    return schemaInput;
  }

  const lookupSet = new Set(
    projectLookupFields.map(field => field.toLowerCase())
  );
  const enumOptions = projectOptions.map(option => ({
    label: option.label,
    value: option.value,
  }));

  const patchProperty = (key: string, propertyInput: unknown): unknown => {
    if (!propertyInput || typeof propertyInput !== "object") {
      return propertyInput;
    }

    const property = propertyInput as Record<string, unknown>;
    const nestedProperties = property.properties;

    let nextProperty: Record<string, unknown> = { ...property };

    if (nestedProperties && typeof nestedProperties === "object") {
      const nextNested: Record<string, unknown> = {};
      Object.entries(nestedProperties as Record<string, unknown>).forEach(
        ([nestedKey, nestedValue]) => {
          nextNested[nestedKey] = patchProperty(nestedKey, nestedValue);
        }
      );
      nextProperty = {
        ...nextProperty,
        properties: nextNested,
      };
    }

    const items = property.items;
    if (items && typeof items === "object") {
      nextProperty = {
        ...nextProperty,
        items: patchProperty(`${key}_item`, items),
      };
    }

    const fieldName = key.toLowerCase();
    const fieldTitle = String(property.title ?? "").toLowerCase();
    const component = String(property["x-component"] ?? "").toLowerCase();
    const isSelectLike =
      component === "select" ||
      component.includes("select") ||
      Array.isArray(property.enum) ||
      !!property["x-reactions"];

    if (isSelectLike && isProjectLikeField(fieldName, fieldTitle, lookupSet)) {
      const componentProps =
        (property["x-component-props"] as
          | Record<string, unknown>
          | undefined) ?? {};

      nextProperty = {
        ...nextProperty,
        enum: enumOptions,
        "x-component": "Select",
        "x-component-props": {
          ...componentProps,
          showSearch: true,
          optionFilterProp: "label",
          filterOption: true,
          placeholder:
            (componentProps.placeholder as string | undefined) ??
            "Choose a project",
          allowClear: true,
        },
      };

      delete nextProperty["x-reactions"];
      delete nextProperty["x-data-source"];
    }

    return nextProperty;
  };

  const schemaRoot = schemaInput as Record<string, unknown>;
  const hasWrappedSchema =
    schemaRoot.schema !== undefined && schemaRoot.schema !== null;
  const rawSchema = (
    hasWrappedSchema ? schemaRoot.schema : schemaRoot
  ) as Record<string, unknown>;

  if (!rawSchema?.properties || typeof rawSchema.properties !== "object") {
    return schemaInput;
  }

  const nextProperties: Record<string, unknown> = {};
  Object.entries(rawSchema.properties as Record<string, unknown>).forEach(
    ([key, value]) => {
      nextProperties[key] = patchProperty(key, value);
    }
  );

  const nextSchema = {
    ...rawSchema,
    properties: nextProperties,
  };

  if (hasWrappedSchema) {
    return {
      ...schemaRoot,
      schema: nextSchema,
    };
  }

  return nextSchema;
}

function collectProjectFieldNames(
  schemaInput: unknown,
  projectLookupFields: string[]
): string[] {
  if (!schemaInput || typeof schemaInput !== "object") {
    return [];
  }

  const lookupSet = new Set(
    projectLookupFields.map(field => field.toLowerCase())
  );
  const names = new Set<string>();

  const walk = (key: string, node: unknown) => {
    if (!node || typeof node !== "object") {
      return;
    }

    const property = node as Record<string, unknown>;
    const fieldName = key.toLowerCase();
    const fieldTitle = String(property.title ?? "").toLowerCase();

    if (isProjectLikeField(fieldName, fieldTitle, lookupSet)) {
      names.add(key);
    }

    if (property.properties && typeof property.properties === "object") {
      Object.entries(property.properties as Record<string, unknown>).forEach(
        ([nestedKey, nestedValue]) => walk(nestedKey, nestedValue)
      );
    }

    if (property.items && typeof property.items === "object") {
      walk(`${key}_item`, property.items);
    }
  };

  const schemaRoot = schemaInput as Record<string, unknown>;
  const rawSchema = (schemaRoot.schema ?? schemaRoot) as Record<
    string,
    unknown
  >;
  if (!rawSchema?.properties || typeof rawSchema.properties !== "object") {
    return [];
  }

  Object.entries(rawSchema.properties as Record<string, unknown>).forEach(
    ([key, value]) => walk(key, value)
  );

  return Array.from(names);
}

const PtsSchemaCrudPage = ({
  schemaName,
  pageTitle,
  pageDescription,
  entityName,
  projectLookupFields = [],
  moveColumnsToEnd = [],
  ensureListColumns = [],
  placeColumnsAfter = {},
  useOpenProjectListInForm = false,
}: PtsSchemaCrudPageProps) => {
  const queryClient = useQueryClient();
  const formCardRef = useRef<HTMLDivElement | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFormHighlighted, setIsFormHighlighted] = useState(false);
  const [selectedProjectFilter, setSelectedProjectFilter] =
    useState<string>("all");
  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName),
    enabled: !!schemaName,
  });

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", schemaName],
    queryFn: () => SchemaService.getColumnGridData(schemaName),
    enabled: !!schemaName,
  });

  const shouldResolveProjectNames = projectLookupFields.length > 0;
  const shouldLoadOpenProjectList =
    shouldResolveProjectNames || useOpenProjectListInForm;

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
    enabled: shouldLoadOpenProjectList,
  });

  const formSchemaJson = useMemo(() => {
    const schemaJson = schemaInfo?.json ?? schemaInfo;
    if (!useOpenProjectListInForm) {
      return schemaJson;
    }

    return injectOpenProjectListIntoSchema(
      schemaJson,
      projectLookupFields,
      projectOptions
    );
  }, [
    projectLookupFields,
    projectOptions,
    schemaInfo,
    useOpenProjectListInForm,
  ]);

  const projectFormFieldNames = useMemo(() => {
    if (!useOpenProjectListInForm) {
      return [] as string[];
    }
    return collectProjectFieldNames(
      schemaInfo?.json ?? schemaInfo,
      projectLookupFields
    );
  }, [projectLookupFields, schemaInfo, useOpenProjectListInForm]);

  useEffect(() => {
    if (!useOpenProjectListInForm || projectFormFieldNames.length === 0) {
      return;
    }

    const dataSource = projectOptions.map(option => ({
      label: option.label,
      value: option.value,
    }));

    projectFormFieldNames.forEach(fieldName => {
      try {
        const field = form.query(fieldName).take() as
          | {
              setDataSource?: (
                options: Array<{ label: string; value: string }>
              ) => void;
              dataSource?: Array<{ label: string; value: string }>;
              loading?: boolean;
              setComponentProps?: (props: Record<string, unknown>) => void;
            }
          | undefined;

        if (!field) {
          return;
        }

        if (typeof field.setDataSource === "function") {
          field.setDataSource(dataSource);
        } else {
          field.dataSource = dataSource;
        }
        field.loading = false;
        field.setComponentProps?.({
          showSearch: true,
          optionFilterProp: "label",
          allowClear: true,
          placeholder: "Choose a project",
        });
      } catch {
        // Field may not be mounted yet for the current form mode.
      }
    });
  }, [
    form,
    projectFormFieldNames,
    projectOptions,
    useOpenProjectListInForm,
    isEditMode,
    editingRecordId,
  ]);

  const projectLookupFieldSet = useMemo(
    () => new Set(projectLookupFields.map(field => field.toLowerCase())),
    [projectLookupFields]
  );

  const projectFilterFieldName = useMemo(() => {
    if (!shouldResolveProjectNames) {
      return null;
    }

    const gridFields =
      columnGridData?.grid?.map((field: { field_name: string }) =>
        String(field.field_name).toLowerCase()
      ) ?? [];

    const matchedField = projectLookupFields
      .map(field => field.toLowerCase())
      .find(field => gridFields.includes(field));

    return matchedField ?? projectLookupFields[0]?.toLowerCase() ?? null;
  }, [columnGridData, projectLookupFields, shouldResolveProjectNames]);

  const listFilter = useMemo(() => {
    const filter: Record<string, string> = {
      ordering: "-created_on",
    };

    if (
      shouldResolveProjectNames &&
      projectFilterFieldName &&
      selectedProjectFilter &&
      selectedProjectFilter !== "all"
    ) {
      filter[`${projectFilterFieldName}__eq`] = selectedProjectFilter;
    }

    return filter;
  }, [
    projectFilterFieldName,
    selectedProjectFilter,
    shouldResolveProjectNames,
  ]);

  const { data: recordData, isLoading: recordLoading } = useQuery({
    queryKey: ["schema-crud-record", schemaName, editingRecordId],
    queryFn: () => {
      if (!editingRecordId) {
        throw new Error(`${entityName} ID is required`);
      }

      return DynamicModuleService.getItemById(schemaName, editingRecordId);
    },
    enabled: !!editingRecordId,
  });

  useEffect(() => {
    if (isEditMode && recordData) {
      form.setValues(removeMetaFields(recordData));
    } else if (!isEditMode) {
      form.reset();
    }
  }, [recordData, form, isEditMode]);

  useEffect(() => {
    if (!isFormHighlighted) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsFormHighlighted(false);
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [isFormHighlighted]);

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["moduleData", schemaName],
    });
  }, [queryClient, schemaName]);

  const focusEditFormCard = useCallback(() => {
    setIsFormHighlighted(true);
    window.requestAnimationFrame(() => {
      formCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const handleEdit = useCallback(
    (id: string | undefined) => {
      if (!id) {
        notify.error(`${entityName} ID is missing`);
        return;
      }

      setEditingRecordId(String(id));
      setIsEditMode(true);
      focusEditFormCard();
    },
    [entityName, focusEditFormCard]
  );

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingRecordId(null);
    setIsFormHighlighted(false);
    form.reset();
  };

  const handleDelete = useCallback(
    async (id: string | undefined) => {
      if (!id) {
        notify.error(`${entityName} ID is missing`);
        return;
      }

      try {
        await DynamicModuleService.deleteItem(schemaName, String(id));
        notify.success(`${entityName} deleted successfully`);
        refreshList();
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : `Failed to delete ${entityName.toLowerCase()}`;
        notify.error(message);
      }
    },
    [entityName, refreshList, schemaName]
  );

  const prepareSubmitValues = (values: Record<string, unknown>) => {
    let modifiedValues = { ...values };

    const schemaJson = schemaInfo?.json as
      | Record<string, unknown>
      | string
      | null
      | undefined;

    if (schemaJson && hasSchema(schemaJson)) {
      const properties =
        (schemaJson.schema as { properties?: Record<string, unknown> })
          ?.properties ?? {};

      Object.values(properties).forEach((property: unknown) => {
        if (
          hasXComponent(property) &&
          property["x-component"] === "CustomUpload"
        ) {
          const value = modifiedValues[property.title];
          if (Array.isArray(value) && value.length > 0 && value[0]?.id) {
            modifiedValues = {
              ...modifiedValues,
              [property.title]: value[0].id,
            };
          }
        }

        if (
          hasXComponent(property) &&
          property["x-component"] === "CustomImageUpload"
        ) {
          const value = modifiedValues[property.title];
          if (value && typeof value === "object" && "id" in value) {
            modifiedValues = {
              ...modifiedValues,
              [property.title]: (value as { id: string }).id,
            };
          }
        }
      });
    }

    return modifiedValues;
  };

  const handleSubmit = async () => {
    try {
      await form.validate();
      const modifiedValues = prepareSubmitValues(
        removeMetaFields(form.values as Record<string, unknown>)
      );
      setIsSubmitting(true);

      if (isEditMode && editingRecordId) {
        if (!checkFormChanges(modifiedValues, recordData, notify)) {
          setIsSubmitting(false);
          return;
        }

        const response = await DynamicModuleService.updateItem(
          schemaName,
          editingRecordId,
          modifiedValues
        );

        if (response) {
          handleCancelEdit();
          refreshList();
        }
      } else {
        const response = await DynamicModuleService.createItem(
          schemaName,
          modifiedValues
        );

        if (response) {
          form.reset();
          refreshList();
        }
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} ${entityName.toLowerCase()}`;
      notify.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = selectedRowKeys.map(key => String(key)).filter(Boolean);

    if (!ids.length) {
      notify.error("Please select at least one record to delete");
      return;
    }

    try {
      await DynamicModuleService.bulkDeleteItems(schemaName, ids);
      notify.success(
        ids.length === 1
          ? `${entityName} deleted successfully`
          : `${ids.length} ${entityName.toLowerCase()} records deleted successfully`
      );
      setSelectedRowKeys([]);
      refreshList();
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to delete selected records";
      notify.error(message);
    }
  };

  const columns: ProColumns<Record<string, unknown>>[] = useMemo(() => {
    const rawFields =
      columnGridData?.grid?.filter(
        (field: { field_name: string }) =>
          !SYSTEM_FIELDS.includes(String(field.field_name).toLowerCase())
      ) ?? [];

    const orderedFields = ensureAndOrderGridFields(
      rawFields,
      ensureListColumns,
      placeColumnsAfter,
      moveColumnsToEnd
    );

    const dataColumns: ProColumns<Record<string, unknown>>[] =
      orderedFields.map(
        (field: { field_name: string; field_title?: string }) => {
          const fieldName = field.field_name.toLowerCase();
          const fieldTitle = (field.field_title ?? "").toLowerCase();
          const isProjectField =
            shouldResolveProjectNames &&
            isProjectLikeField(fieldName, fieldTitle, projectLookupFieldSet);

          return {
            title: field.field_title ?? field.field_name,
            dataIndex: fieldName,
            key: fieldName,
            render: (_: unknown, record: Record<string, unknown>) => {
              const value =
                record?.[fieldName] ?? record?.[field.field_name] ?? "-";
              if (value === null || value === undefined || value === "") {
                return "-";
              }

              let displayValue = "";
              if (typeof value === "object") {
                const label =
                  (value as { label?: string }).label ??
                  (value as { name?: string }).name;
                displayValue = label ? String(label) : JSON.stringify(value);
              } else {
                const stringValue = String(value);
                displayValue = isProjectField
                  ? resolveProjectLabel(stringValue, projectOptions)
                  : stringValue;
              }

              if (isProjectField) {
                const recordId = getRecordId(record);
                return (
                  <button
                    type="button"
                    className="text-primary hover:text-primary/80 cursor-pointer text-left font-medium underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!recordId}
                    title={`Edit ${entityName.toLowerCase()}`}
                    onClick={() => handleEdit(recordId)}
                  >
                    {displayValue}
                  </button>
                );
              }

              return displayValue;
            },
          };
        }
      );

    return [
      ...dataColumns,
      {
        title: "Actions",
        key: "actions",
        width: 120,
        align: "center" as const,
        render: (_: unknown, record: Record<string, unknown>) => {
          const recordId = getRecordId(record);

          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              title={`Edit ${entityName.toLowerCase()}`}
              onClick={() => handleEdit(recordId)}
              disabled={!recordId}
            />,
            <Popconfirm
              key="delete"
              title={`Are you sure you want to delete this ${entityName.toLowerCase()}?`}
              onConfirm={() => handleDelete(recordId)}
              okText="Yes"
              cancelText="No"
              disabled={!recordId}
            >
              <CustomButton
                key="delete-button"
                icon={<Trash size={16} />}
                type="text"
                danger
                title={`Delete ${entityName.toLowerCase()}`}
                disabled={!recordId}
              />
            </Popconfirm>,
          ];
        },
      },
    ];
  }, [
    columnGridData,
    ensureListColumns,
    entityName,
    handleDelete,
    handleEdit,
    moveColumnsToEnd,
    placeColumnsAfter,
    projectLookupFieldSet,
    projectOptions,
    shouldResolveProjectNames,
  ]);

  const isLoading = schemaLoading || (isEditMode && recordLoading);

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">{pageTitle}</h1>
          <p className="text-sm text-[#627384]">{pageDescription}</p>
        </div>
      </div>

      <Card
        ref={formCardRef}
        className={`border bg-white transition-shadow duration-300 ${
          isFormHighlighted || isEditMode
            ? "border-primary ring-primary/30 shadow-md ring-2"
            : "border-gray-200"
        }`}
      >
        <CardContent className="p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-[#111827]">
            {isEditMode ? (
              <>
                <Edit2 className="h-5 w-5" />
                Edit {entityName}
              </>
            ) : (
              <>
                <Plus className="h-5 w-5" />
                Create {entityName}
              </>
            )}
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <SchemaFormView
                  key={`form-${isEditMode ? "edit" : "create"}-${editingRecordId ?? "new"}-${useOpenProjectListInForm && projectOptions.length > 0 ? "projects-ready" : "init"}`}
                  form={form}
                  schemaJson={formSchemaJson}
                />
              </div>

              <div className="flex justify-end gap-3">
                {isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isLoading}
                  className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                >
                  {isEditMode ? (
                    <>
                      <Edit2 className="h-4 w-4" />
                      {isSubmitting ? "Updating..." : `Update ${entityName}`}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {isSubmitting ? "Creating..." : `Create ${entityName}`}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <List className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-[#111827]">
                {entityName} List
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {shouldResolveProjectNames ? (
                <div className="flex min-w-[220px] flex-col gap-1">
                  <Label className="text-xs text-gray-600">Project</Label>
                  <AntSelect
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={selectedProjectFilter}
                    onChange={value => setSelectedProjectFilter(value ?? "all")}
                    className="w-full min-w-[220px]"
                    placeholder="All projects"
                    options={[
                      { value: "all", label: "All projects" },
                      ...projectOptions.map(option => ({
                        value: option.value,
                        label: option.label,
                      })),
                    ]}
                    filterOption={(input, option) =>
                      String(option?.label ?? "")
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                  />
                </div>
              ) : null}

              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Delete selected ${entityName.toLowerCase()} records?`}
                  onConfirm={handleBulkDelete}
                  okText="Yes"
                  cancelText="No"
                >
                  <CustomButton danger icon={<Trash size={16} />}>
                    Delete Selected
                  </CustomButton>
                </Popconfirm>
              )}
            </div>
          </div>

          <ConfigDynamicTable<Record<string, unknown>>
            tableName={schemaName}
            columns={columns}
            headerTitle=""
            rowKey={(record: Record<string, unknown>) => getRecordId(record)}
            filter={listFilter}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PtsSchemaCrudPage;
